import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { apiService } from '../../services/apiService';
import {
  CallInsightsStatsItem,
  ObjFaqItem,
  LandmineItem,
  CompetitorItem,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  getPriorityClasses,
  formatCallInsightsTimestamp,
} from '../adherence/types';
import SentimentRow from '../adherence/SentimentRow';
import { Button } from '../ui/Button';
import TranscriptBlock from '../adherence/TranscriptBlock';

export type HardestItemType = 'objection_faq' | 'landmine' | 'competitor';

interface SessionItemDetail {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  callStartTime: string;
  items: (ObjFaqItem | LandmineItem | CompetitorItem)[];
}

interface HardestItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  columnHeader: string;
  items: CallInsightsStatsItem[];
  itemType: HardestItemType;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
}

// --- Per-item analysis renderers (flat, no accordion wrapper) ---

const ObjFaqAnalysis: React.FC<{ item: ObjFaqItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  return (
    <div className="space-y-3">
      {/* Score + label row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.adherence_label)}`}>
          {formatAdherenceLabel(item.adherence_label)}
        </span>
        <span className="text-xs bg-surface-subtle text-ink-secondary px-2 py-0.5 rounded">{item.category}</span>
        {item.timestamp && (
          <span className="text-xs text-ink-muted">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.adherence_summary && (
        <p className="text-sm text-ink-secondary">{item.adherence_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-success-tint border border-success rounded p-2.5 text-sm">
              <span className="text-success font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-ink">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-error-tint border border-error rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-error font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-ink">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-error' : ''}>
                  <p className="text-xs font-semibold text-error uppercase mb-1">Should Have Said</p>
                  <p className="text-ink">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-ink-muted italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_before}
        after={item.sentiment_after}
        trend={item.sentiment_trend}
      />

      {(item.stored_answer || item.ai_answer) && (
        <div>
          <button
            type="button"
            onClick={() => setShowAnswers((v) => !v)}
            className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
          >
            {showAnswers ? 'Hide Suggested Answers' : 'Show Suggested Answers'}
            <span className="ml-1">{showAnswers ? '▲' : '▼'}</span>
          </button>
          {showAnswers && (
            <div className="mt-2 space-y-2">
              {item.stored_answer && (
                <div className="bg-surface-subtle border border-hairline rounded p-3 text-sm">
                  <p className="font-semibold text-ink-secondary mb-1">Knowledge Base Answer</p>
                  <p className="text-ink">{item.stored_answer}</p>
                </div>
              )}
              {item.ai_answer && (
                <div className="bg-info-tint border border-info rounded p-3 text-sm">
                  <p className="font-semibold text-info mb-1">AI-Generated Answer</p>
                  <p className="text-ink">{item.ai_answer}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

const LandmineAnalysis: React.FC<{ item: LandmineItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  const hasGuidelines =
    (item.kb_can_say && item.kb_can_say.length > 0) ||
    (item.kb_cannot_say && item.kb_cannot_say.length > 0) ||
    !!item.kb_quick_tips;

  return (
    <div className="space-y-3">
      {/* Score + label + meta row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}>
          {formatAdherenceLabel(item.handling_label)}
        </span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getPriorityClasses(item.priority)}`}>
          {item.priority?.toUpperCase() ?? 'UNKNOWN'} Priority
        </span>
        {item.triggered_by && (
          <span className="text-xs text-ink-muted">
            Triggered by: <span className="font-medium capitalize">{item.triggered_by}</span>
          </span>
        )}
        {item.timestamp && (
          <span className="text-xs text-ink-muted">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.handling_summary && (
        <p className="text-sm text-ink-secondary">{item.handling_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-success-tint border border-success rounded p-2.5 text-sm">
              <span className="text-success font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-ink">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-error-tint border border-error rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-error font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-ink">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-error' : ''}>
                  <p className="text-xs font-semibold text-error uppercase mb-1">Should Have Said</p>
                  <p className="text-ink">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-ink-muted italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_at_detection}
        after={item.sentiment_after}
        labelBefore="At Detection"
        labelAfter="After"
      />

      {hasGuidelines && (
        <div>
          <button
            type="button"
            onClick={() => setShowGuidelines((v) => !v)}
            className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
          >
            {showGuidelines ? 'Hide Compliance Guidelines' : 'Show Compliance Guidelines'}
            <span className="ml-1">{showGuidelines ? '▲' : '▼'}</span>
          </button>
          {showGuidelines && (
            <div className="mt-2 space-y-3">
              {item.kb_can_say && item.kb_can_say.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-success uppercase mb-1">Can Say</p>
                  <ul className="space-y-1">
                    {item.kb_can_say.map((phrase, i) => (
                      <li key={i} className="text-sm text-ink bg-success-tint border border-success rounded px-2 py-1">
                        {phrase}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_cannot_say && item.kb_cannot_say.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-error uppercase mb-1">Cannot Say</p>
                  <ul className="space-y-1">
                    {item.kb_cannot_say.map((phrase, i) => (
                      <li key={i} className="text-sm text-ink bg-error-tint border border-error rounded px-2 py-1">
                        {phrase}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_quick_tips && (
                <div className="bg-surface-subtle border border-hairline rounded p-3 text-sm text-ink-secondary">
                  <p className="font-semibold text-ink mb-1">Quick Tips</p>
                  <p>{item.kb_quick_tips}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

const CompetitorAnalysis: React.FC<{ item: CompetitorItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showIntel, setShowIntel] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  const hasIntel =
    (item.kb_advantages && item.kb_advantages.length > 0) ||
    (item.kb_weaknesses && item.kb_weaknesses.length > 0) ||
    !!item.kb_talking_points;

  return (
    <div className="space-y-3">
      {/* Score + label + meta row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}>
          {formatAdherenceLabel(item.handling_label)}
        </span>
        {item.triggered_by && (
          <span className="text-xs text-ink-muted">
            Mentioned by: <span className="font-medium capitalize">{item.triggered_by}</span>
          </span>
        )}
        {item.timestamp && (
          <span className="text-xs text-ink-muted">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.handling_summary && (
        <p className="text-sm text-ink-secondary">{item.handling_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-success-tint border border-success rounded p-2.5 text-sm">
              <span className="text-success font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-ink">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-error-tint border border-error rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-error font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-ink">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-error' : ''}>
                  <p className="text-xs font-semibold text-error uppercase mb-1">Should Have Said</p>
                  <p className="text-ink">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-ink-muted italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_at_detection}
        after={item.sentiment_after}
        labelBefore="At Mention"
        labelAfter="After"
      />

      {hasIntel && (
        <div>
          <button
            type="button"
            onClick={() => setShowIntel((v) => !v)}
            className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
          >
            {showIntel ? 'Hide Competitive Intelligence' : 'Show Competitive Intelligence'}
            <span className="ml-1">{showIntel ? '▲' : '▼'}</span>
          </button>
          {showIntel && (
            <div className="mt-2 space-y-3">
              {item.kb_advantages && item.kb_advantages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-success uppercase mb-1">Our Advantages</p>
                  <ul className="space-y-1">
                    {item.kb_advantages.map((adv, i) => (
                      <li key={i} className="text-sm text-ink bg-success-tint border border-success rounded px-2 py-1">
                        {adv}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_weaknesses && item.kb_weaknesses.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-ink-secondary uppercase mb-1">Their Weaknesses</p>
                  <ul className="space-y-1">
                    {item.kb_weaknesses.map((wk, i) => (
                      <li key={i} className="text-sm text-ink bg-surface-subtle border border-hairline rounded px-2 py-1">
                        {wk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_talking_points && (
                <div className="bg-surface-subtle border border-hairline rounded p-3 text-sm text-ink-secondary">
                  <p className="font-semibold text-ink mb-1">Talking Points</p>
                  <p>{item.kb_talking_points}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

// --- Main modal ---

const HardestItemsModal: React.FC<HardestItemsModalProps> = ({
  isOpen,
  onClose,
  title,
  columnHeader,
  items,
  itemType,
  dateFrom,
  dateTo,
  userId,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sessionData, setSessionData] = useState<Map<number, SessionItemDetail[]>>(new Map());
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedRows(new Set());
      setSessionData(new Map());
      setLoadingRows(new Set());
      setRowErrors(new Map());
    }
  }, [isOpen]);

  const fetchSessionsForRow = useCallback(
    async (rowIdx: number, itemId: string) => {
      setLoadingRows((prev) => new Set(prev).add(rowIdx));
      setRowErrors((prev) => {
        const next = new Map(prev);
        next.delete(rowIdx);
        return next;
      });

      try {
        const params: Record<string, string> = { type: itemType, itemId };
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (userId && userId !== 'all') params.userId = userId;

        const response = await apiService.get('/admin/statistics/hardest-item-sessions', params);

        if (response.success && response.data) {
          setSessionData((prev) => {
            const next = new Map(prev);
            next.set(rowIdx, response.data as SessionItemDetail[]);
            return next;
          });
        } else {
          throw new Error(response.error ?? 'Failed to load session details');
        }
      } catch (err: any) {
        setRowErrors((prev) => {
          const next = new Map(prev);
          next.set(rowIdx, err?.message ?? 'Failed to load session details');
          return next;
        });
      } finally {
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowIdx);
          return next;
        });
      }
    },
    [itemType, dateFrom, dateTo, userId]
  );

  const handleRowToggle = (rowIdx: number, itemId: string) => {
    const isCurrentlyExpanded = expandedRows.has(rowIdx);

    setExpandedRows((prev) => {
      const next = new Set(prev);
      isCurrentlyExpanded ? next.delete(rowIdx) : next.add(rowIdx);
      return next;
    });

    if (!isCurrentlyExpanded && !sessionData.has(rowIdx)) {
      fetchSessionsForRow(rowIdx, itemId);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-panel relative max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="heading-3">{title}</h3>
            <p className="text-sm text-ink-secondary mt-1">
              {items.length} item{items.length !== 1 ? 's' : ''} — click any row to see per-session analysis
            </p>
          </div>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClose}
            type="button"
            className="text-ink-muted hover:text-ink"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-ink-muted">
              Not enough data yet (need 2+ sessions with completed analysis for the same item)
            </div>
          ) : (
            <div>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_100px_80px] gap-2 px-4 py-2 bg-surface-subtle rounded-t-lg border border-hairline border-b-0">
                <span className="text-xs font-semibold text-ink-secondary uppercase">{columnHeader}</span>
                <span className="text-xs font-semibold text-ink-secondary uppercase text-center">Avg Score</span>
                <span className="text-xs font-semibold text-ink-secondary uppercase text-center"># of Times</span>
              </div>

              {/* Rows */}
              <div className="border border-hairline rounded-b-lg overflow-hidden">
                {items.map((item, idx) => {
                  const isExpanded = expandedRows.has(idx);
                  const isLoading = loadingRows.has(idx);
                  const sessions = sessionData.get(idx) ?? [];
                  const rowError = rowErrors.get(idx);

                  return (
                    <div key={idx} className="border-b border-hairline last:border-b-0">
                      {/* Clickable row */}
                      <button
                        type="button"
                        onClick={() => handleRowToggle(idx, item._id)}
                        className="w-full grid grid-cols-[1fr_100px_80px] gap-2 px-4 py-3 text-left hover:bg-surface-subtle transition-colors items-center"
                      >
                        <span className="text-sm text-ink pr-2" title={item._id}>
                          {item._id}
                        </span>
                        <div className="flex justify-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getScoreBadgeClasses(item.avg_score)}`}>
                            {Math.round(item.avg_score)}
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-sm text-ink-secondary font-medium">{item.count}x</span>
                          <span className="text-ink-muted text-xs">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </button>

                      {/* Expanded per-session content */}
                      {isExpanded && (
                        <div className="bg-surface-subtle border-t border-hairline p-4">
                          {isLoading ? (
                            <div className="flex justify-center items-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-signature" />
                              <span className="ml-2 text-sm text-ink-muted">Loading session details...</span>
                            </div>
                          ) : rowError ? (
                            <div className="text-sm text-error text-center py-4">{rowError}</div>
                          ) : sessions.length === 0 ? (
                            <div className="text-sm text-ink-muted text-center py-4">No session details found</div>
                          ) : (
                            <div className="space-y-4">
                              {sessions.map((sessionDetail, sIdx) => (
                                <div
                                  key={sIdx}
                                  className="bg-surface border border-hairline rounded-lg overflow-hidden"
                                >
                                  {/* Session header */}
                                  <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-surface-subtle border-b border-hairline">
                                    <span className="text-sm font-semibold text-ink">
                                      Session: {sessionDetail.sessionName || sessionDetail.sessionId}
                                    </span>
                                    <span className="text-xs text-ink-muted">{sessionDetail.userEmail}</span>
                                    {sessionDetail.callStartTime && (
                                      <span className="text-xs text-ink-muted">
                                        {formatDate(sessionDetail.callStartTime)}
                                      </span>
                                    )}
                                  </div>

                                  {/* Analysis body — one block per occurrence */}
                                  {sessionDetail.items.map((occurrence, occIdx) => (
                                    <div key={occIdx} className="p-4 border-b last:border-b-0">
                                      {sessionDetail.items.length > 1 && (
                                        <p className="text-xs font-semibold text-ink-muted uppercase mb-2">
                                          Occurrence {occIdx + 1} of {sessionDetail.items.length}
                                        </p>
                                      )}
                                      {itemType === 'objection_faq' && (
                                        <ObjFaqAnalysis
                                          item={occurrence as ObjFaqItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                      {itemType === 'landmine' && (
                                        <LandmineAnalysis
                                          item={occurrence as LandmineItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                      {itemType === 'competitor' && (
                                        <CompetitorAnalysis
                                          item={occurrence as CompetitorItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="secondary" onClick={onClose} type="button">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HardestItemsModal;
